/**
 * reducers/index.ts
 * desc: reducers for client-side store
 * note: global definition for all application reducers
 */

import { reducer as toastrReducer } from 'react-redux-toastr';
import type { Reducer } from '@reduxjs/toolkit';
import collectionsReducer from './collections';
import constantsReducer from './constants';
import intentsReducer from './intents';
import localesReducer from './locales';
import modelsReducer from './models';
import ordersReducer from './orders';
import orderReviewReducer from './order-review';
import pathDebugReducer from './path-debug';
import pathsReducer from './paths';
import pathVariablesReducer from './path-variables';
import redactionReducer from './redaction';
import reportsReducer from './reports';
import reviewQueuesReducer from './review-queues';
import rolesReducer from './roles';
import sourcesReducer from './sources';
import sourceTypesReducer from './source-types';
import stepFactoriesReducer from './step-factories';
import testSessionsReducer from './test-sessions';
import trainingEnginesReducer from './training-engines';
import tripsReducer from './trips';
import usersReducer from './users';
import webhooksReducer from './webhooks';
import appReducer from './app';

// unconverted JS reducers are never type-checked, so their state is `unknown`
// in RootState until the slice converts and exports its State type; read those
// slices through a cast at the boundary, never `state.<slice>.<field>` directly
type UntypedReducer = (...args: never[]) => unknown;
const untyped = (reducer: UntypedReducer) => reducer as Reducer<unknown>;

const reducers = {
    app: appReducer,
    collections: collectionsReducer,
    constants: constantsReducer,
    intents: intentsReducer,
    locales: localesReducer,
    models: modelsReducer,
    orders: ordersReducer,
    orderReview: orderReviewReducer,
    pathDebug: pathDebugReducer,
    paths: untyped(pathsReducer),
    pathVariables: pathVariablesReducer,
    redaction: redactionReducer,
    reports: reportsReducer,
    reviewQueues: reviewQueuesReducer,
    roles: rolesReducer,
    sources: untyped(sourcesReducer),
    sourceTypes: sourceTypesReducer,
    stepFactories: stepFactoriesReducer,
    testSessions: testSessionsReducer,
    toastr: toastrReducer,
    trainingEngines: trainingEnginesReducer,
    trips: tripsReducer,
    users: usersReducer,
    webhooks: webhooksReducer,
};


export default reducers;
